'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type ReactElement, createContext } from 'react';
import { Menu } from '@base-ui/react/menu';
import { clsx } from 'clsx';
import {
  createFilter,
  type Filter,
  type FilterFieldConfig,
  type FilterFieldGroup,
  type FilterFieldsInput,
} from './Filters';
import styles from './Filters.module.css';
import menuStyles from './FiltersMenu.module.css';

// ==================== HELPERS ====================

function isGrouped<T>(fields: FilterFieldsInput<T>): fields is FilterFieldGroup<T>[] {
  return fields.length > 0 && 'group' in fields[0];
}

function flattenFields<T>(fields: FilterFieldsInput<T>): FilterFieldConfig<T>[] {
  if (isGrouped(fields)) return fields.flatMap(g => g.fields);
  return fields as FilterFieldConfig<T>[];
}

function getDefaultOperator(field: FilterFieldConfig): string {
  if (field.defaultOperator) return field.defaultOperator;
  if (field.type === 'multiselect') return 'is_any_of';
  if (field.type === 'select') return 'is';
  return 'contains';
}

// ==================== CONTEXT ====================

const FilterPopupContext = createContext<React.RefObject<HTMLElement | null> | null>(null);

// ==================== TYPES ====================

export interface FiltersMenuProps<T = unknown> {
  filters: Filter<T>[];
  fields: FilterFieldsInput<T>;
  onChange: (filters: Filter<T>[]) => void;
  trigger?: ReactNode;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  enableShortcut?: boolean;
  shortcutKey?: string;
  triggerLabel?: string;
  menuLabel?: string;
}

// ==================== ICONS ====================

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="12" height="12" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" aria-hidden>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// ==================== FIELD SUBMENU (in add-filter dropdown) ====================

interface FieldSubmenuProps<T> {
  field: FilterFieldConfig<T>;
  currentValues: T[];
  onToggle: (value: T) => void;
}

function FieldSubmenu<T>({ field, currentValues, onToggle }: FieldSubmenuProps<T>) {
  const [search, setSearch] = useState('');
  const [alignOffset, setAlignOffset] = useState(0);
  const triggerRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useContext(FilterPopupContext);
  const isMulti = field.type === 'multiselect';

  const visibleOptions = search
    ? (field.options ?? []).filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : field.options ?? [];

  const handleOpenChange = (open: boolean) => {
    if (open && popupRef?.current && triggerRef.current) {
      const popupTop = popupRef.current.getBoundingClientRect().top;
      const triggerTop = triggerRef.current.getBoundingClientRect().top;
      setAlignOffset(-(triggerTop - popupTop));
    }
    if (open) {
      // Focus without the browser's default scroll-into-view — the submenu
      // is already positioned in view, so a native autoFocus scroll jump
      // just shifts the page and breaks the click that opened it.
      requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
    }
    if (!open) setTimeout(() => setSearch(''), 150);
  };

  return (
    <Menu.SubmenuRoot modal={false} onOpenChange={handleOpenChange}>
      <Menu.SubmenuTrigger ref={triggerRef} className={clsx(styles.item, menuStyles.submenuTrigger)}>
        {field.icon && <span className={styles.optionIcon}>{field.icon}</span>}
        <span style={{ flex: 1 }}>{field.label}</span>
        {currentValues.length > 0 && (
          <span className={menuStyles.selectedBadge}>{currentValues.length}</span>
        )}
        <span className={menuStyles.chevronRight}><ChevronRightIcon /></span>
      </Menu.SubmenuTrigger>
      <Menu.Portal>
        <Menu.Positioner side="right" align="start" sideOffset={2} alignOffset={alignOffset} collisionPadding={8} positionMethod="fixed">
          <Menu.Popup className={clsx(styles.popup, styles.valuePopup)}>
            {field.searchable !== false && (
              <>
                <div className={styles.searchRow}>
                  <input
                    ref={searchInputRef}
                    className={styles.searchInput}
                    placeholder={`Search ${field.label.toLowerCase()}...`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </div>
                <div className={styles.divider} />
              </>
            )}
            {isMulti && (
              <div className={menuStyles.selectHint}>Select one or more</div>
            )}
            <div className={styles.optionsList}>
              {visibleOptions.length === 0 && <div className={styles.empty}>No results</div>}
              {visibleOptions.map(option => {
                const isSelected = currentValues.includes(option.value);
                return (
                  <Menu.Item
                    key={String(option.value)}
                    className={styles.item}
                    closeOnClick={false}
                    onClick={() => onToggle(option.value)}
                  >
                    {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                    <span style={{ flex: 1 }}>{option.label}</span>
                    {isSelected && <span className={styles.checkmark}><CheckIcon /></span>}
                  </Menu.Item>
                );
              })}
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.SubmenuRoot>
  );
}

// ==================== TEXT FIELD SUBMENU ====================

interface TextFieldSubmenuProps<T> {
  field: FilterFieldConfig<T>;
  currentValues: T[];
  onAdd: (value: string) => void;
}

function TextFieldSubmenu<T>({ field, currentValues, onAdd }: TextFieldSubmenuProps<T>) {
  const [value, setValue] = useState('');
  const [alignOffset, setAlignOffset] = useState(0);
  const triggerRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useContext(FilterPopupContext);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter' && value.trim()) {
      onAdd(value.trim());
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open && popupRef?.current && triggerRef.current) {
      const popupTop = popupRef.current.getBoundingClientRect().top;
      const triggerTop = triggerRef.current.getBoundingClientRect().top;
      setAlignOffset(-(triggerTop - popupTop));
    }
    if (open) {
      requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
    }
    if (!open) setTimeout(() => setValue(''), 150);
  };

  return (
    <Menu.SubmenuRoot modal={false} onOpenChange={handleOpenChange}>
      <Menu.SubmenuTrigger ref={triggerRef} className={clsx(styles.item, menuStyles.submenuTrigger)}>
        {field.icon && <span className={styles.optionIcon}>{field.icon}</span>}
        <span style={{ flex: 1 }}>{field.label}</span>
        {currentValues.length > 0 && (
          <span className={menuStyles.selectedBadge}>{currentValues.length}</span>
        )}
        <span className={menuStyles.chevronRight}><ChevronRightIcon /></span>
      </Menu.SubmenuTrigger>
      <Menu.Portal>
        <Menu.Positioner side="right" align="start" sideOffset={2} alignOffset={alignOffset} collisionPadding={8} positionMethod="fixed">
          <Menu.Popup className={clsx(styles.popup, styles.valuePopup)}>
            <div className={styles.searchRow}>
              <input
                ref={searchInputRef}
                className={styles.searchInput}
                placeholder={field.placeholder ?? `Filter by ${field.label.toLowerCase()}...`}
                value={value}
                onChange={e => setValue(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={handleKeyDown}
              />
            </div>
            {value.trim() && (
              <>
                <div className={styles.divider} />
                <div className={styles.optionsList}>
                  <Menu.Item
                    className={clsx(styles.item, styles.itemActive)}
                    onClick={() => { onAdd(value.trim()); setValue(''); }}
                  >
                    <span style={{ flex: 1 }}>Apply &ldquo;{value.trim()}&rdquo;</span>
                    <span className={styles.checkmark}><CheckIcon /></span>
                  </Menu.Item>
                </div>
              </>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.SubmenuRoot>
  );
}

// ==================== CHIP VALUE DISPLAY (click to edit) ====================

interface ChipValueProps<T> {
  field: FilterFieldConfig<T>;
  values: T[];
  onToggle: (value: T) => void;
  onTextChange: (value: string) => void;
  size: 'sm' | 'default' | 'lg';
}

function ChipValue<T>({ field, values, onToggle, onTextChange, size }: ChipValueProps<T>) {
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  if (field.type === 'text') {
    return (
      <input
        className={styles.segmentInput}
        type="text"
        value={(values[0] as string) ?? ''}
        placeholder={field.placeholder ?? `${field.label}...`}
        onChange={e => onTextChange(e.target.value)}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  const isMulti = field.type === 'multiselect';
  const selectedOptions = (field.options ?? []).filter(o => values.includes(o.value));
  const displayLabel =
    selectedOptions.length === 0 ? 'Select...'
    : selectedOptions.length <= 2 ? selectedOptions.map(o => o.label).join(', ')
    : `${selectedOptions.slice(0, 2).map(o => o.label).join(', ')} +${selectedOptions.length - 2}`;

  const visibleOptions = search
    ? (field.options ?? []).filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : field.options ?? [];

  return (
    <Menu.Root
      modal={false}
      onOpenChange={open => {
        if (open) requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
        if (!open) setTimeout(() => setSearch(''), 150);
      }}
    >
      <Menu.Trigger className={styles.segment}>
        {selectedOptions.length > 0 && (
          <span className={styles.valueIcons}>
            {selectedOptions.slice(0, 3).map(o => o.icon && <span key={String(o.value)}>{o.icon}</span>)}
          </span>
        )}
        {displayLabel}
        <ChevronDownIcon />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="start" sideOffset={4} collisionPadding={8}>
          <Menu.Popup className={clsx(styles.popup, styles.valuePopup)}>
            {field.searchable !== false && (
              <>
                <div className={styles.searchRow}>
                  <input
                    ref={searchInputRef}
                    className={styles.searchInput}
                    placeholder={`Search ${field.label.toLowerCase()}...`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.optionsList}>
              {visibleOptions.length === 0 && <div className={styles.empty}>No results</div>}
              {visibleOptions.map(option => {
                const isSelected = values.includes(option.value);
                return (
                  <Menu.Item
                    key={String(option.value)}
                    className={styles.item}
                    closeOnClick={!isMulti}
                    onClick={() => onToggle(option.value)}
                  >
                    {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                    <span style={{ flex: 1 }}>{option.label}</span>
                    {isSelected && <span className={styles.checkmark}><CheckIcon /></span>}
                  </Menu.Item>
                );
              })}
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

// ==================== MAIN COMPONENT ====================

export function FiltersMenu<T = unknown>({
  filters,
  fields,
  onChange,
  trigger,
  size = 'default',
  className,
  enableShortcut = false,
  shortcutKey,
  triggerLabel = 'Filter',
  menuLabel = 'Filter by',
}: FiltersMenuProps<T>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const popupRef = useRef<HTMLElement>(null);

  const flatFields = useMemo(() => flattenFields(fields), [fields]);
  const fieldsMap = useMemo(
    () => Object.fromEntries(flatFields.map(f => [f.key, f])),
    [flatFields]
  );

  // Filter fields always remain in the menu after being applied. Their
  // submenu reflects the current selection so users can review or change it
  // without first removing the active chip.
  const visibleFields = flatFields;
  const visibleGroups = isGrouped(fields) ? fields : [];

  // Get current values for a field
  const getFieldValues = useCallback(
    (fieldKey: string): T[] => filters.find(f => f.field === fieldKey)?.values ?? [],
    [filters]
  );

  // Toggle a value for a select/multiselect field
  const toggleFieldValue = useCallback((fieldKey: string, value: T) => {
    const field = flatFields.find(f => f.key === fieldKey);
    if (!field) return;
    const isMulti = field.type === 'multiselect';
    const existing = filters.find(f => f.field === fieldKey);

    if (!existing) {
      onChange([...filters, createFilter<T>(fieldKey, getDefaultOperator(field as FilterFieldConfig), [value])]);
      return;
    }

    if (!isMulti) {
      const isCurrentlySelected = existing.values.includes(value);
      if (isCurrentlySelected) {
        onChange(filters.filter(f => f.id !== existing.id));
      } else {
        onChange(filters.map(f => f.id === existing.id ? { ...f, values: [value] } : f));
      }
      return;
    }

    const isSelected = existing.values.includes(value);
    const newValues = isSelected
      ? existing.values.filter(v => v !== value)
      : [...existing.values, value];

    if (newValues.length === 0) {
      onChange(filters.filter(f => f.id !== existing.id));
    } else {
      onChange(filters.map(f => f.id === existing.id ? { ...f, values: newValues } : f));
    }
  }, [filters, flatFields, onChange]);

  // Add a text field chip, or update it when the field is already active.
  const addTextFieldWithValue = useCallback((fieldKey: string, value: string) => {
    if (!value.trim()) return;
    const field = flatFields.find(f => f.key === fieldKey);
    if (!field) return;
    const existing = filters.find(f => f.field === fieldKey);
    if (existing) {
      onChange(filters.map(f => f.id === existing.id ? { ...f, values: [value as T] } : f));
      return;
    }
    onChange([...filters, createFilter<T>(fieldKey, getDefaultOperator(field as FilterFieldConfig), [value as T])]);
  }, [filters, flatFields, onChange]);

  const updateTextValue = useCallback((filterId: string, value: string) => {
    onChange(filters.map(f => f.id === filterId ? { ...f, values: [value as T] } : f));
  }, [filters, onChange]);

  const removeFilter = useCallback((id: string) => {
    onChange(filters.filter(f => f.id !== id));
  }, [filters, onChange]);

  // Keyboard shortcut
  useEffect(() => {
    if (!enableShortcut || !shortcutKey) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
      if (e.key.toLowerCase() === shortcutKey.toLowerCase() && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setMenuOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enableShortcut, shortcutKey]);

  const grouped = isGrouped(fields);
  // Whether any fields are configured at all — the trigger stays visible even
  // once every field has a filter applied, so the menu (and its "already
  // filtered" state) stays reachable. Only hide it when there was never
  // anything to filter by in the first place.
  const hasAnyFields = flatFields.length > 0;
  const hasResults = grouped ? visibleGroups.length > 0 : visibleFields.length > 0;

  function renderFieldItem(field: FilterFieldConfig<T>) {
    if (field.type === 'text') {
      return (
        <TextFieldSubmenu
          key={field.key}
          field={field}
          currentValues={getFieldValues(field.key)}
          onAdd={value => addTextFieldWithValue(field.key, value)}
        />
      );
    }

    const currentValues = getFieldValues(field.key);
    return (
      <FieldSubmenu
        key={field.key}
        field={field}
        currentValues={currentValues}
        onToggle={value => toggleFieldValue(field.key, value)}
      />
    );
  }

  return (
    <FilterPopupContext.Provider value={popupRef}>
      <div className={clsx(styles.root, className)}>
        {hasAnyFields && (
          <Menu.Root
            modal={false}
            open={menuOpen}
            onOpenChange={setMenuOpen}
          >
            {trigger ? (
              <Menu.Trigger render={trigger as ReactElement} />
            ) : (
              <Menu.Trigger className={clsx(styles.addBtn, styles[size])}>
                <FilterIcon />
                {triggerLabel}
                {enableShortcut && shortcutKey && (
                  <span className={styles.shortcutBadge}>{shortcutKey.toUpperCase()}</span>
                )}
              </Menu.Trigger>
            )}
            <Menu.Portal>
              <Menu.Positioner side="bottom" align="start" sideOffset={4} collisionPadding={8}>
                <Menu.Popup ref={popupRef} className={styles.popup} style={{ width: 220 }}>
                  <div className={menuStyles.menuHeading}>
                    {menuLabel}
                    <button
                      type="button"
                      onClick={() => setMenuOpen(false)}
                      className={menuStyles.closeBtn}
                      aria-label="Close filter menu"
                    >
                      ✕
                    </button>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.optionsList}>
                    {!hasResults && <div className={styles.empty}>No filters found</div>}
                    {grouped
                      ? visibleGroups.map((group, idx) => (
                          <div key={group.group}>
                            {idx > 0 && <div className={styles.groupDivider} />}
                            <div className={styles.groupLabel}>{group.group}</div>
                            {group.fields.map(f => renderFieldItem(f))}
                          </div>
                        ))
                      : visibleFields.map(f => renderFieldItem(f))
                    }
                  </div>
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
        )}

        {filters.map(filter => {
          const field = fieldsMap[filter.field] as FilterFieldConfig<T> | undefined;
          if (!field) return null;
          return (
            <div key={filter.id} className={clsx(styles.chip, styles[size])}>
              <span className={styles.chipLabel}>
                {field.icon && <span className={styles.optionIcon}>{field.icon}</span>}
                {field.label}
              </span>
              <ChipValue
                field={field}
                values={filter.values}
                onToggle={value => toggleFieldValue(filter.field, value)}
                onTextChange={value => updateTextValue(filter.id, value)}
                size={size}
              />
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeFilter(filter.id)}
                aria-label={`Remove ${field.label} filter`}
              >
                <XIcon />
              </button>
            </div>
          );
        })}
      </div>
    </FilterPopupContext.Provider>
  );
}
